import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useCMS } from '../context/CMSContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMapPin, FiPhone, FiMail, FiClock, FiSend, FiMessageCircle, FiCheckCircle } = FiIcons;

const Contact = () => {
  const { content } = useCMS();
  const { company, services } = content;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
    urgency: 'normal',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    const form = e.target;
    
    // Encode form data properly for Netlify
    const data = new URLSearchParams();
    data.append('form-name', 'contact-form');
    data.append('name', formData.name);
    data.append('email', formData.email);
    data.append('phone', formData.phone);
    data.append('company', formData.company);
    data.append('service', formData.service);
    data.append('message', formData.message);
    data.append('urgency', formData.urgency);

    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString(),
    })
      .then(() => {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: '',
          urgency: 'normal',
        });
        setIsSubmitting(false);

        setTimeout(() => {
          setSubmitted(false);
        }, 5000);
      })
      .catch((err) => {
        console.error('Form submission error:', err);
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: '',
          urgency: 'normal',
        });
        setIsSubmitting(false);

        setTimeout(() => {
          setSubmitted(false);
        }, 5000);
      });
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: 'easeOut' },
    },
  };

  if (submitted) {
    return (
      <div className="min-h-screen py-12 sm:py-20 bg-gray-50 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl p-8 sm:p-12 shadow-xl text-center max-w-md"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiCheckCircle} className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
            Demande envoyée !
          </h2>
          <p className="text-gray-600 mb-6 text-sm sm:text-base">
            Nous avons bien reçu votre demande. Un de nos experts vous contactera dans les 24 heures.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 sm:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12 sm:mb-16"
        >
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 bg-gradient-to-r from-gold-600 to-gold-500 bg-clip-text text-transparent">
            Contactez-nous
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-2">
            Prêt à améliorer votre performance QHSE ? Nos experts sont là pour vous accompagner. Demandez votre devis gratuit dès maintenant.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 mb-12"
        >
          <motion.div variants={itemVariants} className="lg:col-span-2">
            <motion.div
              className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg"
              whileHover={{
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              }}
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
                Demander un devis
              </h2>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm sm:text-base"
                >
                  {error}
                </motion.div>
              )}

              <form
                onSubmit={handleSubmit}
                name="contact-form"
                method="POST"
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <motion.div whileHover={{ y: -5 }}>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all text-sm sm:text-base"
                      placeholder="Votre nom"
                    />
                  </motion.div>

                  <motion.div whileHover={{ y: -5 }}>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all text-sm sm:text-base"
                      placeholder="votre@email.com"
                    />
                  </motion.div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <motion.div whileHover={{ y: -5 }}>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all text-sm sm:text-base"
                      placeholder="+237 6XX XX XX XX"
                    />
                  </motion.div>

                  <motion.div whileHover={{ y: -5 }}>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Entreprise
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all text-sm sm:text-base"
                      placeholder="Nom de votre entreprise"
                    />
                  </motion.div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <motion.div whileHover={{ y: -5 }}>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Service demandé
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all text-sm sm:text-base"
                    >
                      <option value="">Sélectionner un service</option>
                      {services.items.map((service) => (
                        <option key={service.id} value={service.title}>
                          {service.title}
                        </option>
                      ))}
                    </select>
                  </motion.div>

                  <motion.div whileHover={{ y: -5 }}>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Urgence
                    </label>
                    <select
                      name="urgency"
                      value={formData.urgency}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all text-sm sm:text-base"
                    >
                      <option value="normal">Normal (24-48h)</option>
                      <option value="urgent">Urgent (Dès réception)</option>
                    </select>
                  </motion.div>
                </div>

                <motion.div whileHover={{ y: -5 }}>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    required
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all text-sm sm:text-base"
                    placeholder="Décrivez vos besoins spécifiques..."
                  />
                </motion.div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-gradient-to-r from-gold-500 to-yellow-400 hover:from-gold-600 hover:to-yellow-500 text-gray-900 px-6 py-4 rounded-lg font-semibold transition-all disabled:opacity-50 flex items-center justify-center text-sm sm:text-base"
                >
                  {isSubmitting ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900" />
                  ) : (
                    <>
                      <SafeIcon icon={FiSend} className="w-5 h-5 mr-2" />
                      Envoyer la demande
                    </>
                  )}
                </motion.button>
              </form>
            </motion.div>
          </motion.div>

          <motion.div variants={itemVariants} className="space-y-8">
            <motion.div
              className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg"
              whileHover={{
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              }}
            >
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">
                Informations de contact
              </h3>
              <div className="space-y-4">
                {[
                  {
                    icon: FiMapPin,
                    title: 'Adresse',
                    value: company.address,
                  },
                  {
                    icon: FiPhone,
                    title: 'Téléphones',
                    value: `${company.phone} / ${company.phone2}`,
                  },
                  {
                    icon: FiMail,
                    title: 'Email',
                    value: company.email,
                    link: true,
                  },
                  {
                    icon: FiClock,
                    title: 'Horaires',
                    value: 'Lun - Ven: 8h00 - 17h00 | Sam: 8h00 - 12h00',
                  },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    whileHover={{ x: 5 }}
                  >
                    <SafeIcon icon={item.icon} className="w-5 h-5 text-gold-500 mt-1 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 text-sm sm:text-base">{item.title}</p>
                      {item.link ? (
                        <a
                          href={`mailto:${item.value}`}
                          className="text-gold-500 hover:text-gold-600 break-all text-xs sm:text-sm"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-gray-600 text-xs sm:text-sm">{item.value}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <motion.a
                  href="https://wa.me/237675784656?text=Bonjour,je souhaite obtenir des informations sur vos services QHSE."
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  className="w-full bg-gradient-to-r from-green-600 to-green-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-green-700 hover:to-green-600 transition-all inline-flex items-center justify-center text-sm sm:text-base"
                >
                  <SafeIcon icon={FiMessageCircle} className="w-5 h-5 mr-2" />
                  Chat WhatsApp
                </motion.a>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-2xl p-4 shadow-lg"
        >
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">
            Notre localisation
          </h3>
          <div className="aspect-video bg-gray-200 rounded-lg overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3979.9!2d9.761814!3d4.051978!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sQHSE%20Consulting!5e0!3m2!1sfr!2scm!4v1710000000000"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localisation QHSE Consulting"
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;